import { useEffect, useRef } from "react";
import { socketService } from "../services/socketService";
import { StatusMessage, SocketMessage, InterviewCompleteData, ErrorMessage } from "../types/interview";
import { useInterviewStore, useWebSocketStore, executeOnConnectActions } from "../stores";
import { getFeedbackCopy } from "../utils/feedbackLocalization";
import { getUiCopy, isStartupOverlayMessage, platformStatusKind } from "../i18n/copy";

interface UseSocketListenersProps {
  audioEnabled: boolean;
  processAudioChunk: (data: any) => void;
  addMessage: (...args: any[]) => void;
  handleInterviewEvent: (...args: any[]) => void;
  updateUI: (...args: any[]) => void;
  stopProcessing: () => void;
  updateAnalysis: (...args: any[]) => void;
  setLastQuestionTs: (timestamp: number | null) => void;
  setRecentStatuses: React.Dispatch<React.SetStateAction<string[]>>;
}

export const useSocketListeners = ({
  audioEnabled,
  processAudioChunk,
  addMessage,
  handleInterviewEvent,
  updateUI,
  stopProcessing,
  updateAnalysis,
  setLastQuestionTs,
  setRecentStatuses,
}: UseSocketListenersProps) => {
  // Stable refs to avoid effect remounting
  const addMessageRef = useRef(addMessage);
  const handleInterviewEventRef = useRef(handleInterviewEvent);
  const updateUIRef = useRef(updateUI);
  const stopProcessingRef = useRef(stopProcessing);
  const updateAnalysisRef = useRef(updateAnalysis);

  // Keep refs up-to-date without changing their identity
  useEffect(() => {
    addMessageRef.current = addMessage;
    handleInterviewEventRef.current = handleInterviewEvent;
    updateUIRef.current = updateUI;
    stopProcessingRef.current = stopProcessing;
    updateAnalysisRef.current = updateAnalysis;
  }, [addMessage, handleInterviewEvent, updateUI, stopProcessing, updateAnalysis]);

  // Socket listeners – mount ONCE
  useEffect(() => {
    // Store unsubscribe functions for cleanup
    const unsubscribers: (() => void)[] = [];

    // Connection established
    unsubscribers.push(
      socketService.onConnectionEstablished(() => {
        const state = useWebSocketStore.getState();
        const wasReconnecting =
          state.connectionStatus === "reconnecting" || state.reconnectAttempts > 0;
        state.setConnectionStatus("connected");
        state.resetReconnectAttempts();
        
        // Check if we need to restart the interview due to session loss
        const interviewState = useInterviewStore.getState();
        const hasApplicationData = !!interviewState.applicationData?.application_id;
        const isInProgressState = 
          interviewState.chatState === 'INTERVIEW_STARTED' || 
          interviewState.chatState === 'VALIDATING_ID' ||
          interviewState.chatState === 'STANDARD' ||
          interviewState.chatState === 'VERIFICATION';
        const isStuckInLoading = interviewState.ui.showOverlay &&
          isStartupOverlayMessage(interviewState.ui.overlayMessage);
        const isNotComplete = !interviewState.session.isComplete && 
          interviewState.chatState !== 'COMPLETE' &&
          interviewState.chatState !== 'WAITING_REFERRAL' &&
          interviewState.chatState !== 'WAITING_FEEDBACK' &&
          interviewState.chatState !== 'FEEDBACK_SUBMITTED';
        
        // A backend disconnect clears the ephemeral session. Re-run the start
        // handshake so the backend can reconstruct progress from Salesforce.
        if (
          hasApplicationData &&
          (wasReconnecting || isInProgressState || isStuckInLoading) &&
          isNotComplete
        ) {
          console.log('[Reconnection] Restoring the active interview session...');
          
          // Save application data before resetting (resetInterview clears it)
          const appData = interviewState.applicationData;
          
          // Reset interview state to clear stale flags
          const languageSnapshot = {
            interviewLanguage: interviewState.session.interviewLanguage,
            uiLocale: interviewState.session.uiLocale,
            recognitionLocale: interviewState.session.recognitionLocale,
            htmlLang: interviewState.session.htmlLang,
          };
          useInterviewStore.getState().resetInterview();
          
          // Restore application data after reset
          if (appData?.application_id) {
            useInterviewStore.getState().setApplicationData(appData);
            
            // Reset the interviewStartRequested flag so we can start fresh
            useInterviewStore.getState().updateSession({
              interviewStartRequested: false,
              ...languageSnapshot,
            });
            
            // Trigger restart - this will queue if not connected, or send immediately
            useWebSocketStore.getState().startInterview({
              application_id: appData.application_id,
              job_id: appData.job_id,
              applicant_id: appData.applicant_id,
              applicant_email: appData.applicant_email,
              opportunity_discussed_id: appData.opportunity_discussed_id,
            });
          }
        }
        
        executeOnConnectActions(); // Execute any queued actions
        handleInterviewEventRef.current("CONNECTION_ESTABLISHED");
      })
    );

    // Handle disconnection
    unsubscribers.push(
      socketService.onDisconnect(() => {
        const state = useWebSocketStore.getState();
        if (state.connectionStatus !== "failed") {
          state.setConnectionStatus("reconnecting");
          state.incrementReconnectAttempts();
        }
      })
    );

    // Handle permanent reconnection failure
    unsubscribers.push(
      socketService.onReconnectFailed(() => {
        useWebSocketStore.getState().setConnectionStatus("failed");
      })
    );

    // Status messages with enhanced handling
    unsubscribers.push(
      socketService.onStatus((data: StatusMessage) => {
        const state = useInterviewStore.getState();

        // If the loading overlay is visible, any status message with text should update it.
        if (state.ui.showOverlay && data.text) {
          updateUIRef.current({
            overlayMessage: data.text,
            // Update progress only if it's provided
            ...(data.progress !== undefined && { loadingProgress: data.progress }),
          });
        }

        // Handle specific commands that are not just text updates
        if (data.id === "answer-processing-complete") {
          useInterviewStore.getState().hideSkeleton();
          stopProcessingRef.current();
          useWebSocketStore.getState().setProcessing(false);
          return;
        }

        if (data.id === "transitioning_to_standard") {
          state.showSkeleton("thinking");
          return;
        }

        // Handle different status messages with appropriate skeleton variants
        const statusKind = platformStatusKind(data.text);

        if (statusKind === "thinking") {
          useInterviewStore.getState().showSkeleton("thinking");
          return;
        }

        if (statusKind === "processing") {
          useInterviewStore.getState().showSkeleton("processing");
          return;
        }

        if (statusKind === "analysis") {
          useInterviewStore.getState().showSkeleton("analysis");
          return;
        }

        // Handle "rating-ready" status
        if (data.id === "rating-ready") {
          const feedbackCopy = getFeedbackCopy(state.session.interviewLanguage);
          addMessageRef.current(
            feedbackCopy.prompt,
            "ai", // type
            false, // isHtml
            false, // isReferral
            undefined, // level
            "complete", // phase
            true // isRatingRequest
          );
          updateUIRef.current({ showInputArea: false, showBooleanButtons: false });
          return;
        }

        // Handle "referral-ready" status
        if (data.id === "referral-ready") {
          updateUIRef.current({
            showReferralForm: true,
            showInputArea: false,
            showBooleanButtons: false,
          });
          return;
        }

        // Collect recent status texts for diagnostics (max 5)
        if (data.text) {
          setRecentStatuses((prev) => {
            const next = [data.text!, ...prev].slice(0, 5);
            return next;
          });
        }

        if (data.level === "error") {
          handleInterviewEventRef.current("ERROR_OCCURRED");
        }
      })
    );

    // Backend startup/runtime errors are sent as top-level WebSocket "error" events.
    // Without this listener, the loading overlay can stay stuck on the previous status.
    unsubscribers.push(
      socketService.onError((data: ErrorMessage) => {
        const interviewState = useInterviewStore.getState();
        const text =
          data.text ||
          data.message ||
          getUiCopy(interviewState.session.uiLocale).interviewErrorFallback;

        useInterviewStore.getState().hideSkeleton();
        updateUIRef.current({
          showOverlay: false,
          overlayMessage: "",
          loadingProgress: 0,
          showInputArea: false,
          showBooleanButtons: false,
        });
        addMessageRef.current(text, "status", false, false, "error");
        handleInterviewEventRef.current("ERROR_OCCURRED");
        stopProcessingRef.current();
        useWebSocketStore.getState().setProcessing(false);
      })
    );

    // AI messages with sophisticated flow handling
    unsubscribers.push(
      socketService.onAiMessage((data: SocketMessage) => {
        useInterviewStore.getState().hideSkeleton(); // Hide skeleton on new message

        // Fetch latest UI state to avoid stale closure while keeping effect dependencies minimal
        const currentUI = useInterviewStore.getState().ui;

        if (currentUI.showOverlay && (data.is_first_question === true || data.text)) {
          updateUIRef.current({ loadingProgress: 100 });

          setTimeout(() => {
            updateUIRef.current({ showOverlay: false, overlayMessage: "", loadingProgress: 0 });
          }, 500); // Wait for progress bar animation
        }

        // Determine if this is a guardrail/system check (auth or relevance clarification)
        const isGuardrail = data.guardrail_kind === "timing";
        const guardrailKind = data.guardrail_kind;

        // Add the message to chat
        addMessageRef.current(
          data.text,
          "ai",
          data.is_html,
          false,
          isGuardrail ? "warning" : undefined,
          data.phase as any,
          false,
          data.is_acknowledgment,
          data.is_explanation,
          isGuardrail,
          guardrailKind,
          data.is_question || false // Pass is_question flag
        );

        // If the message is a question, dispatch an event to update interview state.
        if (data.is_question) {
          // Track last question timestamp for timing diag
          setLastQuestionTs(Date.now());
          handleInterviewEventRef.current("QUESTION_RECEIVED", data);
        }

        // Handle phase transitions, ensuring not to re-process question events
        if (data.is_verification_question && !data.is_question) {
          handleInterviewEventRef.current("VERIFICATION_STARTED");
        }

        if ((data.start_standard_phase || data.is_standard_question) && !data.is_question) {
          handleInterviewEventRef.current("STANDARD_PHASE_STARTED");
          if (data.start_standard_phase) {
            updateUIRef.current({ showVerificationHeader: false });
          }
        }

        // Standard-answer processing remains locked until the backend confirms
        // that persistence and TTS work have completed.
        const interviewState = useInterviewStore.getState();
        const waitsForAnswerCompletion =
          data.phase === "standard" && interviewState.session.isProcessing;
        if (!waitsForAnswerCompletion) {
          stopProcessingRef.current();
          useWebSocketStore.getState().setProcessing(false);
        }

        // 🎵 TTS: Process AI message text for speech synthesis
        if (audioEnabled && data.text) {
          // Note: TTS will be triggered by the backend automatically for AI messages
          // The audio chunks will come via the audio_chunk WebSocket event
        }
      })
    );

    // Interview completion
    unsubscribers.push(
      socketService.onInterviewComplete((data: InterviewCompleteData) => {
        // Hide any active skeleton when interview completes
        useInterviewStore.getState().hideSkeleton();

        handleInterviewEventRef.current("INTERVIEW_COMPLETED");
        useWebSocketStore.getState().setProcessing(false);

        if (data.final_analysis) {
          // First, update the state with the analysis content
          updateAnalysisRef.current({ content: data.final_analysis });
          // Then, immediately add it as a message to be displayed
          addMessageRef.current(data.final_analysis, "ai", true);
        } else if (data.message) {
          // Fallback for pre-completed interviews that only send a message
          addMessageRef.current(
            data.message,
            "ai",
            !!data.is_html // respect HTML flag if provided
          );
        }

        updateUIRef.current({
          showInputArea: false,
          showBooleanButtons: false,
        });
      })
    );

    // Streaming partial tokens
    unsubscribers.push(
      socketService.onPartialToken(({ text }) => {
        // Append chunk to last AI message
        useInterviewStore.getState().appendToLastAiMessage(text);
      })
    );

    // Audio chunks for TTS playback
    unsubscribers.push(
      socketService.onAudioChunk((data) => {
        if (audioEnabled) {
          processAudioChunk(data);
        }
      })
    );

    // Cleanup listeners on unmount or dependency change
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [processAudioChunk, audioEnabled, setLastQuestionTs, setRecentStatuses]); // Audio-related dependencies
};
